import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSecret, updateSecret, removeSecret, updateSecretValue } from './requests';
import { toast } from 'react-toastify';
import { QueryKey, Secret, UpdateSecretRequest, UpdateSecretValueRequest } from 'api/model';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.Secrets;

type Props = {
  secretId: string;
  secret?: Secret;
};

const useSecret = ({ secretId, secret: initialSecret }: Props) => {
  const queryClient = useQueryClient();

  const { data: latestSecret, isLoading } = useQuery({
    queryKey: [queryKey, { id: secretId }],
    queryFn: () => getSecret(secretId),
    enabled: !!secretId && !initialSecret,
  });

  const secret = latestSecret ?? initialSecret;

  const update = useMutation({
    mutationFn: (secret: UpdateSecretRequest) => updateSecret(secretId, secret),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Secret successfully updated');
    },
    onError: errorHandler('Updating secret failed'),
  });

  const updateValue = useMutation({
    mutationFn: (model: UpdateSecretValueRequest) => updateSecretValue(secretId, model),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Secret value successfully updated');
    },
    onError: errorHandler('Updating secret value failed'),
  });

  const remove = useMutation({
    mutationFn: () => removeSecret(secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Secret successfully removed');
    },
    onError: errorHandler('Removing secret failed'),
  });

  return { secret, isLoading, remove, update, updateValue };
};

export default useSecret;
